import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

// Phase-1 scaffold. The workspace shell, context propagation, and tab nav are
// real now (S3); each tab's data wiring lands in its own board story. A
// placeholder names the story that fills it so the shell is reviewable today.
export function WorkspaceTab({
  title,
  story,
  note,
}: {
  title: string;
  story: string;
  note?: string;
}) {
  return (
    <Card className="p-6">
      <h2 className="font-head text-lg font-semibold mb-1">{title}</h2>
      <p className="text-sm text-muted-foreground">
        {note ?? `Campaign-scoped ${title.toLowerCase()} land in ${story}.`}
      </p>
    </Card>
  );
}

type AudienceCard = { segment: string; label: string; reason: string; count: number };
// Decision 1B: best-effort live block from the Sales CRM — null whenever the
// CRM is unconfigured/unreachable, in which case the cards silently show
// internal counts only.
type CrmStats = {
  leads: number;
  deals: number;
  dealsWon: number;
  tenant: string;
  fetchedAt: string;
};

export function OverviewTab() {
  const { campaign, campaignId } = useWorkspace();
  // #2504-2506: only roles that can actually send packs get the CTA
  // (issuance.send = super_admin / ae / people_ops).
  const { sendPlaylist } = useWorkspaceCan();
  const { data } = useQuery({
    queryKey: ["audience", campaignId],
    queryFn: () =>
      api.get<{ items: AudienceCard[]; crm?: CrmStats | null }>(
        `/api/admin/campaigns/${campaignId}/audience`,
      ),
  });
  const cards = data?.items ?? [];
  const crm = data?.crm ?? null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="font-head text-lg font-semibold mb-3">Overview</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm max-w-xl">
          <dt className="text-muted-foreground">Public name</dt>
          <dd>{campaign.publicName ?? "—"}</dd>
          <dt className="text-muted-foreground">Industry</dt>
          <dd>{campaign.industry ?? "—"}</dd>
          <dt className="text-muted-foreground">Consent tier</dt>
          <dd>{campaign.consentTier?.replace(/_/g, " ")}</dd>
          <dt className="text-muted-foreground">Persona</dt>
          <dd>{campaign.persona?.replace(/_/g, " ") ?? "—"}</dd>
        </dl>
      </Card>

      <div>
        <h3 className="font-head text-base font-semibold mb-1">
          Who can benefit from this campaign's knowledge?
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Audience Intelligence — send the right playlist to the right audience.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {cards.map((c) => (
            <Card key={c.segment} className="p-4 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{c.label}</span>
                <span className="text-sm font-semibold text-gold">{c.count}</span>
              </div>
              {c.segment === "lead" && crm && (
                <p className="text-xs text-muted-foreground">
                  Live from Sales CRM: {crm.leads} lead{crm.leads === 1 ? "" : "s"}
                </p>
              )}
              {c.segment === "existing_client" && crm && (
                <p className="text-xs text-muted-foreground">
                  Live from Sales CRM: {crm.dealsWon} won deal{crm.dealsWon === 1 ? "" : "s"}
                </p>
              )}
              <p className="text-xs text-muted-foreground flex-1">{c.reason}</p>
              {sendPlaylist && (
                <Link
                  to={`/admin/issuances/new?campaignId=${campaignId}&segment=${c.segment}`}
                  className="text-xs px-2 py-1 rounded-md border border-admin-rule hover:bg-foreground/5 self-start"
                >
                  Send Playlist
                </Link>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
